/**
 * Mirrors the ACTIVE player-kanban board into the sessionStorage route-param
 * mirror so a page reload lands the user back on the same pipeline (SE-6916).
 *
 * Before this hook the active board lived only in `activeBoardIdVar`, an
 * in-memory Apollo reactive var. That survives in-app navigation — its stated
 * purpose — but dies on a hard reload, so the screen fell back to the first
 * board of the source type. Nothing durable recorded the user's choice, which
 * is also why the reporter's "this should be applied to every update" holds:
 * any action followed by a refresh lost the selection for the same reason.
 *
 * The mirror is the ONLY sink. Writing the id back into the URL is forbidden —
 * see the remark on the hook below for the render loop it causes.
 * @module features/player-kanban/hooks/useBoardSelectionPersistence
 */

import { useEffect, useRef } from "react";

import { persistRouteParam } from "@/utils/persistedRouteParams";

import {
  BOARD_ID_PARAM,
  PLAYER_KANBAN_ROUTE_KEY,
} from "../constants/routeParams";

/**
 * Keeps the sessionStorage board mirror in step with the board the screen has
 * actually resolved.
 * @param currentBoardId - Id of the active board, or `undefined` while boards
 *   are still loading / no board is active.
 * @returns Nothing; the effect writes the storage mirror as a side effect.
 * @remarks
 * - Writing the resolved id back into the URL is PROHIBITED, and removing that
 *   write is what closed SE-6916 round 2. The reader, `useBoardDeepLink`,
 *   consumes `useLocalSearchParams`, so a URL write here is an echo back into
 *   the reader — and that echo lands one render out of phase with
 *   `useBoardManager`'s own restore. With two or more boards and a non-default
 *   selection the two sides alternate forever (the reader applies the seed, we
 *   write the fallback, the reader then sees the fallback as the live param and
 *   applies it, we write the seed, …), which is the "Maximum update depth
 *   exceeded" crash measured on staging. Neither side's ref guard can break it:
 *   each guards the value the OTHER side is about to change. A URL param is
 *   INPUT to this screen (the SE-4994 deep link) and never OUTPUT.
 * - The mirror alone carries reload survival, so nothing is lost by dropping
 *   the URL tier: Expo Router on web canonicalizes the address during route
 *   hydration and strips the param via `history.replaceState` BEFORE React
 *   renders, so `?boardId=` was never readable on the reload it was meant to
 *   survive. `readInitialRouteParam` reads the mirror instead.
 * - Keyed on the RESOLVED board rather than on each `switchBoard` caller. Chip
 *   and tab clicks, board creation, delete-then-fallback, a source-type flip
 *   and the `?boardId=` deep link all converge on the active board, so writing
 *   here covers every one of them with a single effect — and the mirror
 *   self-heals to whatever the app really settled on (including the fallback
 *   taken after a stale id failed to match).
 * - Ref-guarded on a genuine change to a new, truthy id, mirroring the filter
 *   reset in `MainContainer`: a transient board refetch (`id -> null -> same
 *   id`) must not churn storage.
 * - sessionStorage (not localStorage) is the storage tier by design — see the
 *   `utils/persistedRouteParams` module remark. "Remember the pipeline while
 *   the user is in this browsing session"; a brand-new tab starts at the
 *   screen's default rather than resurrecting a board from days ago.
 * - Native is unaffected: `persistRouteParam` is a no-op where there is no
 *   `window`.
 * - Known limitation on very large board lists: `useAllMyKanbanBoards` walks
 *   the connection page by page, and the board list is reported as loaded once
 *   page one lands (SE-6575). A restored id that only exists on a later page
 *   therefore cannot be matched yet, and the write below re-points the mirror
 *   at the first-page fallback. This is the same window in which the SE-4994
 *   deep link already misses.
 */
export function useBoardSelectionPersistence(
  currentBoardId: string | undefined
): void {
  // Last id written, so an unrelated re-render does not re-issue the write.
  const lastPersistedBoardIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!currentBoardId) return;
    if (lastPersistedBoardIdRef.current === currentBoardId) return;
    lastPersistedBoardIdRef.current = currentBoardId;

    persistRouteParam(PLAYER_KANBAN_ROUTE_KEY, BOARD_ID_PARAM, currentBoardId);
  }, [currentBoardId]);
}
